import type { FirstTouch } from './firstTouch'

export type AuthMethod = 'email' | 'google' | 'siwe'

type DataLayerEvent = Record<string, unknown>

/**
 * GA4 event for a completed Privy sign-in, pushed through GTM's dataLayer:
 * `sign_up` when the backend created the account, `login` otherwise.
 * Carries the stored first-touch tags. Never carries emails or ids.
 */
export const buildAuthEvent = ({
  isNewUser,
  method,
  firstTouch,
}: {
  isNewUser: boolean
  method: AuthMethod | null
  firstTouch: FirstTouch | null
}): DataLayerEvent => ({
  event: isNewUser ? 'sign_up' : 'login',
  method: method ?? undefined,
  utm_source: firstTouch?.utmSource ?? undefined,
  utm_medium: firstTouch?.utmMedium ?? undefined,
  utm_campaign: firstTouch?.utmCampaign ?? undefined,
  utm_content: firstTouch?.utmContent ?? undefined,
  utm_term: firstTouch?.utmTerm ?? undefined,
  landing_path: firstTouch?.landingPath ?? undefined,
})

export const pushAuthEvent = (
  event: Parameters<typeof buildAuthEvent>[0],
  target: { dataLayer?: DataLayerEvent[] } | undefined = typeof window !== 'undefined'
    ? (window as unknown as { dataLayer?: DataLayerEvent[] })
    : undefined,
): void => {
  if (!target) return
  // GTM loads lazily; events pushed before it loads are queued and replayed.
  target.dataLayer = target.dataLayer || []
  target.dataLayer.push(buildAuthEvent(event))
}
